/**
 * Who is signed in.
 *
 * Deliberately separate from `app_context`, and mounted OUTSIDE it. `AppProvider` calls
 * `/api/bootstrap` the moment it mounts; that call is now guarded, so mounting it before we know
 * whether anyone is signed in produces a guaranteed 401 on every visit to the login page — which
 * the session guard would then turn into a redirect to the page you are already on.
 *
 * The session itself is never read from storage. It lives in an HttpOnly cookie the browser will
 * not show us, so first paint asks the server once and believes the answer.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { fetchSession, logout as postLogout, type SessionInfo } from '../api/auth_api'

interface AuthContextValue {
  /** True until the first `/auth/session` answers. Render nothing decisive before then. */
  checking: boolean
  session: SessionInfo
  /** Re-read the session from the server — called after a successful login. */
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [checking, setChecking] = useState(true)
  const [session, setSession] = useState<SessionInfo>({ authenticated: false })

  const refresh = useCallback(async () => {
    setSession(await fetchSession())
    setChecking(false)
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signOut = useCallback(async () => {
    await postLogout()
    setSession({ authenticated: false })
    //  A full document load, not a client-side route change. Every provider below holds data for
    //  the user who just left — reference data, cached lists, open modals — and unmounting them
    //  individually is a long list nobody will keep current. Reloading discards all of it at once.
    window.location.assign('/login')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ checking, session, refresh, signOut }),
    [checking, session, refresh, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
